import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, timeout } from 'rxjs';
import { API_BASE_URL } from '../tokens/api-base-url.token';
import {
  BrdStatus,
  BulkApplyRulePayload,
  CopyRulesPayload,
  ProjectBrd,
  ProjectRule,
  RuleCategory,
  RulePriority,
  UpsertBrdPayload,
  UpsertRulePayload,
} from '../models/rules.models';

@Injectable({ providedIn: 'root' })
export class RulesService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly requestTimeoutMs = 8000;

  private readonly priorityMap: Record<number, RulePriority> = {
    1: 'Low',
    2: 'Medium',
    3: 'High',
    4: 'Critical',
  };

  private readonly statusMap: Record<number, BrdStatus> = {
    0: 'Draft',
    1: 'UnderReview',
    2: 'Approved',
    3: 'Deprecated',
  };

  getCategories(): Observable<RuleCategory[]> {
    return this.http
      .get<RuleCategory[]>(`${this.apiBaseUrl}/api/rules/categories`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  getRules(projectId: string): Observable<ProjectRule[]> {
    return this.http
      .get<ProjectRule[]>(`${this.apiBaseUrl}/api/rules/projects/${projectId}/rules`)
      .pipe(
        timeout(this.requestTimeoutMs),
        map((rules) => rules.map((r) => this.normalizeRule(r))),
      );
  }

  upsertRule(projectId: string, payload: UpsertRulePayload): Observable<ProjectRule> {
    const base = `${this.apiBaseUrl}/api/rules/projects/${projectId}/rules`;
    const req = payload.id
      ? this.http.put<ProjectRule>(`${base}/${payload.id}`, payload)
      : this.http.post<ProjectRule>(base, payload);
    return req.pipe(
      timeout(this.requestTimeoutMs),
      map((r) => this.normalizeRule(r)),
    );
  }

  deleteRule(projectId: string, ruleId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.apiBaseUrl}/api/rules/projects/${projectId}/rules/${ruleId}`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  copyRules(targetProjectId: string, payload: CopyRulesPayload): Observable<ProjectRule[]> {
    return this.http
      .post<ProjectRule[]>(
        `${this.apiBaseUrl}/api/rules/projects/${targetProjectId}/rules/copy`,
        payload,
      )
      .pipe(
        timeout(this.requestTimeoutMs),
        map((rules) => rules.map((r) => this.normalizeRule(r))),
      );
  }

  bulkApplyRule(payload: BulkApplyRulePayload): Observable<ProjectRule[]> {
    return this.http
      .post<ProjectRule[]>(`${this.apiBaseUrl}/api/rules/rules/bulk-apply`, payload)
      .pipe(
        timeout(this.requestTimeoutMs),
        map((rules) => rules.map((r) => this.normalizeRule(r))),
      );
  }

  getBrds(projectId: string): Observable<ProjectBrd[]> {
    return this.http
      .get<ProjectBrd[]>(`${this.apiBaseUrl}/api/rules/projects/${projectId}/brds`)
      .pipe(
        timeout(this.requestTimeoutMs),
        map((brds) => brds.map((b) => this.normalizeBrd(b))),
      );
  }

  upsertBrd(projectId: string, payload: UpsertBrdPayload): Observable<ProjectBrd> {
    const base = `${this.apiBaseUrl}/api/rules/projects/${projectId}/brds`;
    const req = payload.id
      ? this.http.put<ProjectBrd>(`${base}/${payload.id}`, payload)
      : this.http.post<ProjectBrd>(base, payload);
    return req.pipe(
      timeout(this.requestTimeoutMs),
      map((b) => this.normalizeBrd(b)),
    );
  }

  approveBrd(projectId: string, brdId: string, approvedBy: string): Observable<ProjectBrd> {
    return this.http
      .post<ProjectBrd>(
        `${this.apiBaseUrl}/api/rules/projects/${projectId}/brds/${brdId}/approve`,
        { approvedBy },
      )
      .pipe(
        timeout(this.requestTimeoutMs),
        map((b) => this.normalizeBrd(b)),
      );
  }

  deleteBrd(projectId: string, brdId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.apiBaseUrl}/api/rules/projects/${projectId}/brds/${brdId}`)
      .pipe(timeout(this.requestTimeoutMs));
  }

  private normalizeRule(rule: ProjectRule): ProjectRule {
    return { ...rule, priority: this.normalizePriority(rule.priority) };
  }

  private normalizeBrd(brd: ProjectBrd): ProjectBrd {
    return { ...brd, status: this.normalizeStatus(brd.status) };
  }

  private normalizePriority(value: RulePriority | number): RulePriority {
    if (typeof value === 'number') return this.priorityMap[value] ?? 'Medium';
    return value;
  }

  private normalizeStatus(value: BrdStatus | number): BrdStatus {
    if (typeof value === 'number') return this.statusMap[value] ?? 'Draft';
    return value;
  }
}
